// Skrypty Segmentify wstrzykujemy DOKŁADNIE RAZ na cykl życia strony, nawet gdy
// useSegmentify() zawołane w kilku miejscach (app.vue globalnie + testowa strona
// /segmentify-search). Bez tej bramki oba wywołania zdublowałyby SDK.
let segmentifyInjected = false

/**
 * Snippety z panelu Segmentify — CELOWO z różnymi schematami URL:
 *   segmentify.js  → `https://cdn.segmentify.com/{id}/…`
 *   search.js/css  → `//cdn.segmentify.com/{id}/…` (PROTOCOL-RELATIVE)
 *
 * NIE unifikować na `https://`. Załadowany SDK sam dogrywa wyszukiwarkę i chroni się
 * przed zdublowaniem guardem dopasowującym DOKŁADNY string atrybutu src:
 *   `document.querySelector('script[src="' + segmentifyCDNUrl + apiKey + '/search.js"]')`
 * gdzie `segmentifyCDNUrl === "//cdn.segmentify.com/"`. Nasz tag z `https://` guarda NIE
 * zaspokaja → SDK dokłada drugi `<script id="segmentify-search" src="//cdn…/search.js">`,
 * a search.js wykonany po raz drugi rzuca `Error: search.js already exists` (widget
 * wyszukiwarki nie startuje). Skutek uboczny: ten wstrzyknięty przez SDK tag skryptu
 * dubluje id `segmentify-search` z kontenerem widgetu (`document.getElementById` trafia
 * wtedy na skrypt, nie na kontener).
 */
const SDK_URL = (accountId: string) =>
  `https://cdn.segmentify.com/${accountId}/segmentify.js`
const SEARCH_JS_URL = (accountId: string) =>
  `//cdn.segmentify.com/${accountId}/search.js`
const SEARCH_CSS_URL = (accountId: string) =>
  `//cdn.segmentify.com/${accountId}/search.css`

function injectSegmentify(accountId: string) {
  if (segmentifyInjected) return
  segmentifyInjected = true

  const link = document.createElement("link")
  link.rel = "stylesheet"
  // setAttribute, nie `link.href = …` — liczy się DOSŁOWNA wartość atrybutu (patrz wyżej).
  link.setAttribute("href", SEARCH_CSS_URL(accountId))
  document.head.appendChild(link)

  // Kolejność: SDK najpierw, ale oba tagi lądują w DOM w JEDNYM ticku — gdy pobrany
  // segmentify.js zaczyna się wykonywać, tag search.js już jest w <head>, więc guard SDK
  // go widzi i wyszukiwarki nie dogrywa po raz drugi.
  for (const src of [SDK_URL(accountId), SEARCH_JS_URL(accountId)]) {
    const script = document.createElement("script")
    script.setAttribute("src", src)
    script.async = true
    document.head.appendChild(script)
  }
}

/**
 * Segmentify — bezpośrednie osadzenie skryptów per-brand (BEZ GTM).
 *
 * Account ID czytane z runtimeConfig.public.segmentifyAccountId (env
 * NUXT_PUBLIC_SEGMENTIFY_ACCOUNT_ID), więc inventory/swarm ustala je przy starcie
 * kontenera — bez rebuildu obrazu. Puste = no-op (tak jest na dev; prod ma osobne
 * konto per brand: lavmag i youlook).
 *
 * Skrypty: segmentify.js (rekomendacje/personalizacja) + search.js / search.css
 * (wyszukiwarka) — trafiają do <head>, dokładnie jak snippety z panelu Segmentify.
 * Kontener widgetu wyszukiwarki (<div id="segmentify-search" class="segmentify-search-main">)
 * renderuje WYŁĄCZNIE strona /segmentify-search (app/pages/segmentify-search.vue), nie
 * app.vue — poza nią kod ładuje tylko SDK. Zwraca `enabled`, by strona wiedziała, czy
 * renderować kontener.
 *
 * ŁADOWANIE: OD RAZU na starcie klienta (przy hydracji), na WSZYSTKICH trasach — bez
 * czekania na akcję użytkownika. Wcześniej wstrzyknięcie było odroczone do pierwszej
 * interakcji (`onFirstInteraction`, `fallbackMs: null`) ze względu na CWV, ale to
 * oznaczało, że user, który nie scrolluje/nie klika, NIE dostaje ani rekomendacji, ani
 * wyszukiwarki, a sam SDK startuje z opóźnieniem (widgety „doskakiwały" po scrollu).
 * Decyzja: Segmentify to element funkcjonalny sklepu, więc ładujemy go zawsze i od razu.
 *
 * KOSZT (świadomy): ~187 KB skryptów + ~240 ms na wątku głównym wraca do okna pomiaru
 * Lighthouse (TBT, potencjalnie LCP). Skrypty są `async` i wstrzykiwane po hydracji
 * (nie blokują parsowania HTML ani SSR). Jeśli PageSpeed znów będzie problemem, wracać
 * NIE do bramki interakcji, a do lżejszych środków (np. preconnect do cdn.segmentify.com,
 * ograniczenie widgetów w panelu). GTM i Sentry Replay ODROCZONE zostają bez zmian —
 * `onFirstInteraction` nadal ich używa.
 *
 * CSP: pod 'strict-dynamic' skrypt wstrzyknięty przez zaufany (nonce'owany) runtime
 * Nuxta jest autoryzowany; host *.segmentify.com dokładany do script-/style-/connect-/
 * font-src runtime'owo per-brand (server/utils/segmentifyCsp.ts). UWAGA: ładuje się
 * BEZ bramki zgody (decyzja biznesowa), inaczej niż edrone / Meta Pixel.
 */
export function useSegmentify(): { enabled: boolean } {
  const accountId = useRuntimeConfig().public.segmentifyAccountId
  if (!accountId) return { enabled: false }

  if (import.meta.client) injectSegmentify(accountId)

  return { enabled: true }
}
